import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Grid, Card, CardContent, Box, Button,
  Chip, Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemIcon, ListItemText, Divider, Alert,
  CircularProgress, Tabs, Tab, LinearProgress, FormControl,
  InputLabel, Select, MenuItem, TextField, CardActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import {
  AttachMoney, TrendingUp, TrendingDown, AccountBalance, Receipt,
  FileDownload, Print, DateRange, Star,
  CheckCircle, Pending, Schedule, MonetizationOn, Payment,
  CreditCard, AccountBalanceWallet, Analytics, GetApp,
  Visibility, FilterList, Engineering, ElectricalServices,
  LocalFireDepartment, Plumbing, Assignment, Assessment
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import inspectorService from '../services/inspectorService';
import DialogCloseButton from '../components/common/DialogCloseButton';
import { brand, text, border } from '../theme/colors';

const InspectorEarningsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [paymentDetailsDialog, setPaymentDetailsDialog] = useState(false);
  const [withdrawDialog, setWithdrawDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  // Filter states
  const [monthFilter, setMonthFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Data states
  const [earnings, setEarnings] = useState({});
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [monthlyBreakdown, setMonthlyBreakdown] = useState([]);
  const [typeBreakdown, setTypeBreakdown] = useState([]);

  // Sample comprehensive earnings data
  const sampleEarningsData = {
    overview: {
      totalEarnings: 28500,
      currentMonth: 4200,
      availableBalance: 3750,
      pendingPayments: 1250,
      averagePerInspection: 285,
      thisMonthInspections: 18,
      lastMonthInspections: 16,
      growthRate: 12.5,
      qualityBonus: 420,
      completionBonus: 180
    },
    paymentHistory: [
      {
        id: 'PAY-001',
        inspectionId: 'INSP-001',
        projectTitle: 'Office Complex - Structural',
        inspectionType: 'structural',
        date: '2025-02-10',
        amount: 350,
        status: 'paid',
        client: 'Corporate Developments Ltd',
        paymentMethod: 'bank_transfer',
        qualityScore: 98,
        bonusAmount: 35,
        processingTime: 2
      },
      {
        id: 'PAY-002',
        inspectionId: 'INSP-002',
        projectTitle: 'Shopping Mall - Electrical',
        inspectionType: 'electrical',
        date: '2025-02-08',
        amount: 280,
        status: 'paid',
        client: 'Retail Development Corp',
        paymentMethod: 'bank_transfer',
        qualityScore: 95,
        bonusAmount: 28,
        processingTime: 1
      },
      {
        id: 'PAY-003',
        inspectionId: 'INSP-003',
        projectTitle: 'Residential Complex - Fire Safety',
        inspectionType: 'fire_safety',
        date: '2025-02-05',
        amount: 220,
        status: 'paid',
        client: 'Housing Development Co',
        paymentMethod: 'mobile_money',
        qualityScore: 92,
        bonusAmount: 22,
        processingTime: 3
      },
      {
        id: 'PAY-004',
        inspectionId: 'INSP-004',
        projectTitle: 'Industrial Warehouse - Structural',
        inspectionType: 'structural',
        date: '2025-02-03',
        amount: 400,
        status: 'pending',
        client: 'Industrial Holdings Ltd',
        paymentMethod: 'bank_transfer',
        qualityScore: 96,
        bonusAmount: 40,
        processingTime: 0
      },
      {
        id: 'PAY-005',
        inspectionId: 'INSP-005',
        projectTitle: 'School Building - Accessibility',
        inspectionType: 'accessibility',
        date: '2025-01-28',
        amount: 180,
        status: 'paid',
        client: 'Education Ministry',
        paymentMethod: 'bank_transfer',
        qualityScore: 89,
        bonusAmount: 18,
        processingTime: 4
      },
      {
        id: 'PAY-006',
        inspectionId: 'INSP-006',
        projectTitle: 'Hospital Wing - Plumbing',
        inspectionType: 'plumbing',
        date: '2025-01-25',
        amount: 240,
        status: 'paid',
        client: 'Health Ministry',
        paymentMethod: 'bank_transfer',
        qualityScore: 94,
        bonusAmount: 24,
        processingTime: 2
      }
    ],
    monthlyBreakdown: [
      { month: 'Jan 2025', inspections: 16, earnings: 3800, bonus: 380, avgScore: 93.2 },
      { month: 'Dec 2024', inspections: 14, earnings: 3200, bonus: 320, avgScore: 91.8 },
      { month: 'Nov 2024', inspections: 18, earnings: 4100, bonus: 410, avgScore: 94.5 },
      { month: 'Oct 2024', inspections: 15, earnings: 3600, bonus: 360, avgScore: 92.1 },
      { month: 'Sep 2024', inspections: 20, earnings: 4800, bonus: 480, avgScore: 95.3 },
      { month: 'Aug 2024', inspections: 17, earnings: 4000, bonus: 400, avgScore: 93.8 }
    ],
    typeBreakdown: [
      { type: 'structural', count: 28, earnings: 9800, percentage: 34.4, avgAmount: 350 },
      { type: 'electrical', count: 24, earnings: 6720, percentage: 23.6, avgAmount: 280 },
      { type: 'fire_safety', count: 20, earnings: 4400, percentage: 15.4, avgAmount: 220 },
      { type: 'plumbing', count: 18, earnings: 4320, percentage: 15.2, avgAmount: 240 },
      { type: 'accessibility', count: 15, earnings: 2700, percentage: 9.5, avgAmount: 180 },
      { type: 'general', count: 8, earnings: 1600, percentage: 5.6, avgAmount: 200 }
    ]
  };

  // Load earnings data
  useEffect(() => {
    const loadEarningsData = async () => {
      try {
        setLoading(true);
        console.log('Loading earnings data for inspector:', user?.id);
        
        setEarnings(sampleEarningsData.overview);
        setPaymentHistory(sampleEarningsData.paymentHistory);
        setMonthlyBreakdown(sampleEarningsData.monthlyBreakdown);
        setTypeBreakdown(sampleEarningsData.typeBreakdown);
        
      } catch (err) {
        setError('Failed to load earnings data');
        console.error('Error loading earnings data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadEarningsData();
  }, [user?.id]);

  const getStatusColor = (status) => {
    const colors = {
      'paid': 'success',
      'pending': 'warning',
      'processing': 'info',
      'failed': 'error'
    };
    return colors[status] || 'default';
  };

  // Load earnings data from API
  useEffect(() => {
    const loadEarningsData = async () => {
      try {
        setLoading(true);
        const response = await inspectorService.getMyEarnings();
        
        if (response.success && response.data) {
          const summary = response.data.summary || {};
          setEarnings({
            totalEarnings: parseFloat(summary.totalCredited) || 0,
            currentMonth: parseFloat(summary.thisMonth) || 0,
            availableBalance: parseFloat(summary.availableForWithdrawal) || 0,
            pendingPayments: parseFloat(summary.pending) || 0,
            lastMonth: parseFloat(summary.lastMonth) || 0,
            thisWeek: parseFloat(summary.thisWeek) || 0,
            thisMonthInspections: response.data.breakdown?.[0]?.inspections || 0,
            lastMonthInspections: response.data.breakdown?.[1]?.inspections || 0,
            growthRate: 0,
            qualityBonus: 0,
            completionBonus: 0
          });
          setPaymentHistory(response.data.recentPayments || []);
          setMonthlyBreakdown(response.data.breakdown || []);
        } else {
          // Use sample data
          setEarnings(sampleEarningsData.overview);
          setPaymentHistory(sampleEarningsData.paymentHistory);
          setMonthlyBreakdown(sampleEarningsData.monthlyBreakdown);
          setTypeBreakdown(sampleEarningsData.typeBreakdown);
        }
      } catch (err) {
        console.error('Load earnings error:', err);
        // Use sample data on error
        setEarnings(sampleEarningsData.overview);
        setPaymentHistory(sampleEarningsData.paymentHistory);
        setMonthlyBreakdown(sampleEarningsData.monthlyBreakdown);
        setTypeBreakdown(sampleEarningsData.typeBreakdown);
      } finally {
        setLoading(false);
      }
    };
    loadEarningsData();
  }, []);

  const getInspectionTypeIcon = (type) => {
    const icons = {
      'structural': <Engineering />,
      'electrical': <ElectricalServices />,
      'fire_safety': <LocalFireDepartment />,
      'plumbing': <Plumbing />,
      'accessibility': <Assignment />,
      'general': <Assessment />
    };
    return icons[type] || <Assignment />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewPaymentDetails = (payment) => {
    setSelectedPayment(payment);
    setPaymentDetailsDialog(true);
  };

  const handleWithdrawFunds = () => {
    setWithdrawDialog(true);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} sx={{ color: brand.primary }} />
        <Typography variant="h6" sx={{ mt: 2, color: text.secondary }}>Loading earnings data...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: text.primary }}>
            Earnings & Payments
          </Typography>
          <Typography variant="body1" sx={{ color: text.secondary }}>
            Track your inspection earnings, payment history, and financial performance
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<FileDownload />} sx={{ borderColor: brand.primary, color: brand.primary, borderWidth: 2, fontWeight: 600, '&:hover': { backgroundColor: brand.primary, color: 'white', borderColor: brand.primary } }}>
            Export Report
          </Button>
          <Button variant="contained" startIcon={<AccountBalanceWallet />} onClick={handleWithdrawFunds} sx={{ bgcolor: brand.primary, fontWeight: 700, '&:hover': { bgcolor: brand.primaryDark } }}>
            Withdraw Funds
          </Button>
        </Box>
      </Box>

      {/* Earnings Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ border: '2px solid #e1e8ed', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', transition: 'transform 0.3s ease, box-shadow 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', borderColor: text.primary } }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AttachMoney sx={{ fontSize: 40, color: brand.primary, opacity: 0.6, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: brand.primary }}>
                {formatCurrency(earnings.totalEarnings)}
              </Typography>
              <Typography variant="body2" sx={{ color: text.secondary }}>
                Total Earnings
              </Typography>
              <Typography variant="caption" sx={{ color: '#43B581' }}>
                From {earnings.thisMonthInspections + earnings.lastMonthInspections} inspections
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ border: '2px solid #e1e8ed', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', transition: 'transform 0.3s ease, box-shadow 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', borderColor: text.primary } }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <DateRange sx={{ fontSize: 40, color: '#FAA61A', opacity: 0.6, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#FAA61A' }}>
                {formatCurrency(earnings.currentMonth)}
              </Typography>
              <Typography variant="body2" sx={{ color: text.secondary }}>
                This Month
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 1 }}>
                <TrendingUp fontSize="small" sx={{ color: '#43B581' }} />
                <Typography variant="caption" sx={{ color: '#43B581' }}>
                  +{earnings.growthRate}% from last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ border: '2px solid #e1e8ed', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', transition: 'transform 0.3s ease, box-shadow 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', borderColor: text.primary } }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccountBalance sx={{ fontSize: 40, color: '#43B581', opacity: 0.6, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#43B581' }}>
                {formatCurrency(earnings.availableBalance)}
              </Typography>
              <Typography variant="body2" sx={{ color: text.secondary }}>
                Available Balance
              </Typography>
              <Typography variant="caption" sx={{ color: text.secondary }}>
                Ready for withdrawal
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ border: '2px solid #e1e8ed', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', transition: 'transform 0.3s ease, box-shadow 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', borderColor: text.primary } }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Pending sx={{ fontSize: 40, color: '#FAA61A', opacity: 0.6, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#FAA61A' }}>
                {formatCurrency(earnings.pendingPayments)}
              </Typography>
              <Typography variant="body2" sx={{ color: text.secondary }}>
                Pending Payments
              </Typography>
              <Typography variant="caption" sx={{ color: text.secondary }}>
                Processing soon
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: border.light, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            '& .MuiTabs-indicator': { backgroundColor: brand.primary },
            '& .MuiTab-root.Mui-selected': { color: brand.primary, fontWeight: 600 },
            '& .MuiTab-root': { color: text.secondary }
          }}
        >
          <Tab icon={<AttachMoney />} label="Payment History" />
          <Tab icon={<Assessment />} label="Monthly Breakdown" />
          <Tab icon={<Analytics />} label="Inspection Types" />
          <Tab icon={<Receipt />} label="Tax Information" />
        </Tabs>
      </Box>

      {/* Payment History Tab */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Payment History</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Month</InputLabel>
                  <Select
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(e.target.value)}
                    label="Month"
                  >
                    <MenuItem value="all">All Months</MenuItem>
                    <MenuItem value="2025-02">Feb 2025</MenuItem>
                    <MenuItem value="2025-01">Jan 2025</MenuItem>
                    <MenuItem value="2024-12">Dec 2024</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    label="Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="structural">Structural</MenuItem>
                    <MenuItem value="electrical">Electrical</MenuItem>
                    <MenuItem value="fire_safety">Fire Safety</MenuItem>
                    <MenuItem value="plumbing">Plumbing</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Quality Score</TableCell>
                    <TableCell>Bonus</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentHistory.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>{formatDate(payment.date)}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {payment.projectTitle}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {payment.client}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getInspectionTypeIcon(payment.inspectionType)}
                          <Typography variant="body2">
                            {payment.inspectionType.replace('_', ' ').toUpperCase()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatCurrency(payment.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Star sx={{ color: 'gold', fontSize: 16 }} />
                          <Typography variant="body2">{payment.qualityScore}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#43B581' }}>
                          {formatCurrency(payment.bonusAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={payment.status.toUpperCase()} 
                          color={getStatusColor(payment.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<Visibility />}
                          onClick={() => handleViewPaymentDetails(payment)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Monthly Breakdown Tab */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Monthly Earnings Breakdown
            </Typography>
            
            <Grid container spacing={3}>
              {monthlyBreakdown.map((month, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: brand.primary, fontWeight: 700 }}>
                          {month.month}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Star sx={{ color: 'gold', fontSize: 16 }} />
                          <Typography variant="body2">{month.avgScore}%</Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Earnings
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#43B581' }}>
                          {formatCurrency(month.earnings)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Inspections: {month.inspections}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Avg: {formatCurrency(month.earnings / month.inspections)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Quality Bonus:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#43B581' }}>
                          {formatCurrency(month.bonus)}
                        </Typography>
                      </Box>
                      
                      <LinearProgress
                        variant="determinate"
                        value={month.avgScore}
                        sx={{ height: 6, borderRadius: 3, backgroundColor: border.light, '& .MuiLinearProgress-bar': { backgroundColor: brand.primary } }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Monthly Performance Trend
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                📈 Your earnings have grown {((monthlyBreakdown[0]?.earnings - monthlyBreakdown[1]?.earnings) / monthlyBreakdown[1]?.earnings * 100).toFixed(1)}% compared to last month. Keep up the excellent quality work!
              </Alert>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Inspections</TableCell>
                      <TableCell align="right">Base Earnings</TableCell>
                      <TableCell align="right">Quality Bonus</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Avg Score</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {monthlyBreakdown.map((month, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{month.month}</TableCell>
                        <TableCell align="right">{month.inspections}</TableCell>
                        <TableCell align="right">{formatCurrency(month.earnings - month.bonus)}</TableCell>
                        <TableCell align="right" sx={{ color: '#43B581', fontWeight: 500 }}>
                          {formatCurrency(month.bonus)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatCurrency(month.earnings)}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Star sx={{ color: 'gold', fontSize: 14 }} />
                            {month.avgScore}%
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Inspection Types Tab */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Earnings by Inspection Type
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {typeBreakdown.map((type, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {getInspectionTypeIcon(type.type)}
                        <Typography variant="h6" sx={{ ml: 1, textTransform: 'capitalize' }}>
                          {type.type.replace('_', ' ')}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Earnings
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: brand.primary }}>
                          {formatCurrency(type.earnings)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {type.percentage}% of total earnings
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Inspections: {type.count}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Avg: {formatCurrency(type.avgAmount)}
                        </Typography>
                      </Box>
                      
                      <LinearProgress
                        variant="determinate"
                        value={type.percentage}
                        sx={{ height: 6, borderRadius: 3, mt: 2, backgroundColor: border.light, '& .MuiLinearProgress-bar': { backgroundColor: brand.primary } }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Type Performance Analysis
              </Typography>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Inspection Type</TableCell>
                      <TableCell align="right">Count</TableCell>
                      <TableCell align="right">Total Earnings</TableCell>
                      <TableCell align="right">Average per Inspection</TableCell>
                      <TableCell align="right">Percentage of Total</TableCell>
                      <TableCell align="center">Performance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {typeBreakdown.map((type, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getInspectionTypeIcon(type.type)}
                            <Typography sx={{ textTransform: 'capitalize' }}>
                              {type.type.replace('_', ' ')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{type.count}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatCurrency(type.earnings)}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(type.avgAmount)}</TableCell>
                        <TableCell align="right">{type.percentage}%</TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={type.avgAmount >= 300 ? 'High Value' : type.avgAmount >= 200 ? 'Medium Value' : 'Standard'}
                            color={type.avgAmount >= 300 ? 'success' : type.avgAmount >= 200 ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Alert severity="success" sx={{ mt: 3 }}>
                💡 <strong>Insight:</strong> Structural inspections are your highest-earning specialty at {formatCurrency(typeBreakdown[0]?.avgAmount)} per inspection. Consider focusing on this area for maximum earnings.
              </Alert>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tax Information Tab */}
      {activeTab === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Tax Information & Documents
            </Typography>
            
            {/* Tax Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Receipt sx={{ fontSize: 40, color: brand.primary, opacity: 0.6, mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600, color: brand.primary }}>
                      {formatCurrency(earnings.totalEarnings * 0.85)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Taxable Income (2024)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      85% of gross earnings
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <AccountBalance sx={{ fontSize: 40, color: '#FAA61A', opacity: 0.6, mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#FAA61A' }}>
                      {formatCurrency(earnings.totalEarnings * 0.15)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Estimated Tax (15%)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Based on self-employment rate
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <MonetizationOn sx={{ fontSize: 40, color: '#43B581', opacity: 0.6, mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#43B581' }}>
                      {formatCurrency(earnings.totalEarnings * 0.7)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Net Income
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      After estimated taxes
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            {/* Tax Documents Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Available Tax Documents
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Receipt />
                  </ListItemIcon>
                  <ListItemText 
                    primary="2024 Annual Earnings Statement"
                    secondary="Complete breakdown of all inspection earnings and bonuses"
                  />
                  <Button variant="outlined" size="small" startIcon={<FileDownload />} sx={{ borderColor: brand.primary, color: brand.primary, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}>
                    Download
                  </Button>
                </ListItem>
                
                <Divider />
                
                <ListItem>
                  <ListItemIcon>
                    <Assessment />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Monthly Earnings Summary (Current Year)"
                    secondary="Month-by-month breakdown for tax filing purposes"
                  />
                  <Button variant="outlined" size="small" startIcon={<FileDownload />} sx={{ borderColor: brand.primary, color: brand.primary, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}>
                    Download
                  </Button>
                </ListItem>
                
                <Divider />
                
                <ListItem>
                  <ListItemIcon>
                    <Analytics />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Inspection Type Report"
                    secondary="Detailed report by inspection categories for expense deductions"
                  />
                  <Button variant="outlined" size="small" startIcon={<FileDownload />} sx={{ borderColor: brand.primary, color: brand.primary, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}>
                    Download
                  </Button>
                </ListItem>
                
                <Divider />
                
                <ListItem>
                  <ListItemIcon>
                    <AccountBalanceWallet />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Payment Method Summary"
                    secondary="Summary of payments received by method (for banking records)"
                  />
                  <Button variant="outlined" size="small" startIcon={<FileDownload />} sx={{ borderColor: brand.primary, color: brand.primary, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}>
                    Download
                  </Button>
                </ListItem>
              </List>
            </Box>
            
            {/* Tax Tips Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Tax Tips for Quality Inspectors
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  💡 Deductible Expenses for Quality Inspectors:
                </Typography>
                <Typography variant="body2" component="div">
                  • Professional certification maintenance fees<br/>
                  • Safety equipment and tools<br/>
                  • Vehicle expenses for site visits<br/>
                  • Professional development courses<br/>
                  • Professional liability insurance<br/>
                  • Mobile phone and internet costs (business use portion)
                </Typography>
              </Alert>
              
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  ⚠️ Important Tax Considerations:
                </Typography>
                <Typography variant="body2" component="div">
                  • As an independent contractor, you're responsible for self-employment tax<br/>
                  • Consider making quarterly estimated tax payments<br/>
                  • Keep detailed records of all business expenses<br/>
                  • Consult with a tax professional for personalized advice
                </Typography>
              </Alert>
              
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button variant="contained" startIcon={<FileDownload />} sx={{ bgcolor: brand.primary, fontWeight: 700, '&:hover': { bgcolor: brand.primaryDark } }}>
                  Generate Tax Package
                </Button>
                <Button variant="outlined" startIcon={<Assessment />} sx={{ borderColor: brand.primary, color: brand.primary, borderWidth: 2, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}>
                  Request Tax Summary
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Payment Details Dialog */}
      <Dialog
        open={paymentDetailsDialog}
        onClose={() => setPaymentDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => setPaymentDetailsDialog(false)} />
        <DialogTitle>Payment Details</DialogTitle>
        <DialogContent dividers>
          {selectedPayment && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Payment Information
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Payment ID"
                        secondary={selectedPayment.id}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Amount"
                        secondary={formatCurrency(selectedPayment.amount)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Status"
                        secondary={
                          <Chip 
                            label={selectedPayment.status?.toUpperCase()} 
                            color={getStatusColor(selectedPayment.status)}
                            size="small"
                          />
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Project Information
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Project"
                        secondary={selectedPayment.projectTitle}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Project Owner"
                        secondary={selectedPayment.client}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Quality Score"
                        secondary={`${selectedPayment.qualityScore}%`}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDetailsDialog(false)} sx={{ color: text.secondary }}>Close</Button>
          <Button variant="outlined" startIcon={<Receipt />} sx={{ borderColor: brand.primary, color: brand.primary, borderWidth: 2, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}>
            Download Receipt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Withdraw Funds Dialog */}
      <Dialog
        open={withdrawDialog}
        onClose={() => setWithdrawDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={() => setWithdrawDialog(false)} />
        <DialogTitle>Withdraw Funds</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Available Balance: {formatCurrency(earnings.availableBalance)}
          </Typography>
          
          <TextField
            fullWidth
            label="Withdrawal Amount"
            type="number"
            sx={{ mb: 3, mt: 2 }}
            inputProps={{ max: earnings.availableBalance }}
          />
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Withdrawal Method</InputLabel>
            <Select label="Withdrawal Method" defaultValue="">
              <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
              <MenuItem value="mobile_money">Mobile Money</MenuItem>
              <MenuItem value="paypal">PayPal</MenuItem>
            </Select>
          </FormControl>
          
          <Alert severity="info">
            Withdrawals are processed within 1-3 business days. A small processing fee may apply.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawDialog(false)} sx={{ color: text.secondary }}>Cancel</Button>
          <Button variant="contained" startIcon={<MonetizationOn />} sx={{ bgcolor: brand.primary, fontWeight: 700, '&:hover': { bgcolor: brand.primaryDark } }}>
            Request Withdrawal
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InspectorEarningsPage; 